"use client";
import React, { useState } from "react";
import Image from "next/image";
import { ClockIcon, CopyIcon, StarIcon, ArrowRightIcon } from "@/utils/icons";
import { useRouter } from "next/navigation";

interface OfferCardProps {
  storeLogo: string | null;
  storeName: string;
  title: string;
  description: string;
  discountType: string;
  discountTypeFlat: string;
  discount: string;
  discountTypeUnit: string;
  oldValue: string;
  oldValueUnit: "price" | "percentage";
  couponCode: string;
  badge: string;
  validTill: string;
  slug: string;
  allowRedirectUrl?: boolean;
  redirectUrl?: string;
  rating?: number;
  usedBy?: number;
}

interface Offer {
  slug: string;
  couponCode: string | null;
  discountType: string | null;
  storeName: string;
}

const OfferCard: React.FC<OfferCardProps> = ({
  storeLogo,
  storeName,
  title,
  description,
  discountType,
  discountTypeFlat,
  discount,
  discountTypeUnit,
  oldValue,
  oldValueUnit,
  couponCode,
  badge,
  validTill,
  slug,
  rating,
  // usedBy,
}) => {
  const [copied, setCopied] = useState<string | null>(null);
    const router = useRouter();

  const offerData: Offer = {
    slug: slug,
    couponCode: couponCode,
    discountType: discountType,
    storeName: storeName,
  };

  const handleCopy = async (code: string) => {
    let success = false;
    // Try Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(code);
        success = true;
      } catch { /* ignored */ }
    }
    // Fallback for non-secure or unsupported browsers
    if (!success) {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        success = true;
      } catch { /* ignored */ }
      document.body.removeChild(textarea);
    }
    if (success) {
      setCopied(code);
      setTimeout(() => setCopied(null), 1200);
    }
  };

  const handleCode = (offer: Offer) => {
    if (offer.couponCode) {
      handleCopy(offer.couponCode);
    }

    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(internalOfferUrl, "_blank", "noopener,noreferrer");
    window.location.href = couponLoaderUrl;
  };

  const handleGeneral = (offer: Offer) => {
    if (offer.couponCode ) {
      handleCopy(offer.couponCode);
    }

    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(couponLoaderUrl, "_blank", "noopener,noreferrer");
    window.location.href = internalOfferUrl;
  };

  const handleViewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (couponCode && discountType==='code') {
      handleCode(offerData);
    } else {
      handleGeneral(offerData);
    }
  };

  const handleShowAllOffer = () => {
    const showAllOffer = `/store/${storeName}/`;
    // window.location.href = showAllOffer;
    router.push(showAllOffer)
  };

  const RenderDiscountPercentage = () => {
    if (!oldValue || !discount) return null;

    const oldVal = Number(oldValue);
    const discountVal = Number(discount);

    if (isNaN(oldVal) || isNaN(discountVal)) return null;
    
    if (discountVal < 1 && oldVal > 0) {
      return (
        <div className="w-full">
          <p className="gap-1 flex items-baseline text-xs ">
            <span className="text-base font-semibold text-primary-700">
              ₹{oldVal.toFixed(2)}
            </span>
          </p>
        </div>
      );
    }
    
    if (discountTypeUnit === "percentage" && oldValueUnit === "price") {
      const discountAmount = (oldVal * discountVal) / 100;
      const newVal = oldVal - discountAmount;
      if (newVal <= 0)
        return <p className="font-bold text-red-600">100% off</p>;

      const percentage = Math.round(((oldVal - newVal) / oldVal) * 100);

      return (
        <div className="w-full">
          <p className="gap-1 flex items-baseline text-xs ">
            <span className="text-base font-semibold text-primary-700">
              ₹{newVal.toFixed(2)}
            </span>
            <span className="line-through text-gray-500">
              ₹{oldVal.toFixed(2)}
            </span>
            <span className="font-medium text-primary-600">
              {percentage}% off
            </span>{" "}
          </p>
        </div>
      );
    }

    if (discountTypeUnit === "price" && oldValueUnit === "price") {
      const newVal = oldVal - discountVal;
      if (newVal <= 0)
        return <p className="font-bold text-red-600">100% off</p>;

      const percentage = Math.round(((oldVal - newVal) / oldVal) * 100);

      return (
        <div className="">
          <p className="gap-1 flex items-baseline text-xs ">
            <span className="text-base font-semibold text-primary-700">
              ₹{newVal.toFixed(2)}
            </span>
            <span className="line-through text-gray-500">
              ₹{oldVal.toFixed(2)}
            </span>
            <span className="font-medium text-primary-600">
              {percentage}% off
            </span>{" "}
          </p>
        </div>
      );
    }

    if (discountTypeUnit === "percentage" && oldValueUnit === "percentage") {
      const change = Math.abs(oldVal - discountVal);
      const direction = oldVal > discountVal ? "↓" : "↑";
      const percentageChange = ((change / oldVal) * 100).toFixed(1);

      return (
        <p className="gap-1 text-sm font-medium text-primary-600">
          <span className="line-through text-gray-500">
            {oldVal}%
          </span>{" "}
          →
          <span className=" text-base font-medium text-primary-600">
            {discountVal}%{" "}
          </span>
          <span className="ml-1 text-xs">
            {direction} {percentageChange}%
          </span>
        </p>
      );
    }

    if (discountTypeUnit === "price" && oldValueUnit === "percentage") {
      // const newVal = oldVal - discountVal;
      return (
        <p className="text-sm font-medium text-primary-600">
          <span className="line-through ">
            {oldVal}%
          </span>{" "} → ₹{discountVal}{" "}
          <span className="text-gray-500 text-xs ml-1">(Fixed drop)</span>
        </p>
      );
    }

    return null;
  };

  if (!couponCode && !discount) return null;

  return (
    <div
      className="text-gray-700 w-full bg-white rounded-xl shadow-lg flex flex-col justify-between 
             border border-gray-100 
             hover:scale-[101%] hover:border-primary-400 focus-within:shadow-xl focus-within:border-primary-500 
             transition-all duration-300 ease-in-out relative overflow-hidden"
    >
      {/* Mobile View Structure (Visible below MD) */}
      <div className="md:hidden">
        {badge && (
          <div
            className="absolute top-0 right-0 z-10"
          >
            <div
              className="flex items-center justify-center bg-gradient-to-r from-primary-500 to-cyan-400 rounded-tr-xl rounded-bl-lg h-7 px-2 py-0.5 shadow-md border border-primary-200"
            >
              <span className="text-[10px] font-bold uppercase text-white leading-none">
                {badge}
              </span>
            </div>
          </div>
        )}
        <div className="flex p-2 pr-6 pt-2">
          <div className="flex-shrink-0 w-1/4 h-20 bg-gray-100 rounded-md flex items-center justify-center text-center p-1 border-r border-gray-300">
            {storeLogo && (
              <Image
                src={storeLogo}
                alt={storeName}
                width={64}
                height={64}
                className="object-contain max-h-12 max-w-full"
              />
            )}
          </div>
          <div className="flex-1 px-3 py-1 flex flex-col justify-center">
            <p className="text-sm font-medium text-gray-800 lg:line-clamp-2">
              {title}
            </p>
            <p className="text-xs font-medium text-gray-500 lg:line-clamp-2">
              {description}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-100 p-1">
          {/* Action Buttons Row */}
          <div className="grid grid-cols-12 gap-2 mb-2 px-2">
            <div className="col-span-9">

            <button
              onClick={handleShowAllOffer}
              className="text-[10px] text-blue-600 font-semibold w-full hover:text-blue-800 transition-colors p-2 border border-blue-200 rounded-lg flex items-center justify-center text-center leading-tight"
            >
              <span className="break-words capitalize">
                See All {storeName.replaceAll("-offers", " ").replaceAll('-',' ')} Offers
              </span>
              <ArrowRightIcon className="w-3 h-3 ml-1 flex-shrink-0" />
            </button>                
            </div>
             <div className="col-span-3 w-full">
            <button
              className={`w-full flex-1 py-2 rounded-lg bg-primary-600 text-white font-bold cursor-pointer text-[10px] tracking-wide shadow-md 
                                hover:bg-primary-700 transition-all duration-200 hover:shadow-lg flex items-center justify-center`}
              onClick={handleViewClick}
            >
              <span className="relative z-10">
                {couponCode && discountType === "code" ? "GET CODE" : "VIEW DEAL"}
                
              </span>
            </button>


            </div>
          </div>
        </div>
      </div>

      {/* Desktop View Structure (Hidden below MD) */}
      <div className="hidden md:flex flex-col h-full">
        {/* Top Section: Logo, Title, Description */}
        <div className="flex flex-col items-center justify-center pt-6 pb-3 px-4 flex-grow">
          {storeLogo && (
            <div className="w-16 h-12 flex items-center justify-center mb-2">
              <Image
                src={storeLogo}
                alt={storeName}
                width={64}
                height={48}
                className="object-contain max-h-12"
              />
            </div>
          )}
          <div className="font-bold text-sm text-gray-900 text-center mb-1 line-clamp-1">
            {title}
          </div>
          <div className="text-xs capitalize text-gray-700 text-center mb-2 font-medium">
            {storeName.replaceAll("-", " ")}
          </div>
          <div className="text-xs text-gray-600 text-center line-clamp-2 h-8">
            {description}
          </div>
        </div>

        {/* Middle Section: Offer/Coupon Display */}
        <div className="px-4 pb-4">
          {discount && discountType === "general" && (
            <div className="w-full flex flex-col items-center mb-3">
              {discount && oldValue && (
                <div className="flex w-full justify-center mb-1">
                  {RenderDiscountPercentage()}
                </div>
              )}
              {/* Main Discount Headline */}
              {(Number(discount) > 1) ? <div className="capitalize w-full text-center text-sm font-bold text-primary-700 p-2 border-2 border-primary-100 bg-primary-50 rounded-lg">
                <span className="text-xs text-gray-500 font-medium block">Discount</span>
                Get {discountTypeFlat} {discount}{" "}
                {discountTypeUnit === "percentage" ? "%" : "₹"} Off
              </div>
                :
                <div className="capitalize w-full text-center text-sm font-bold text-primary-700 p-2 border-2 border-primary-100 bg-primary-50 rounded-lg">
                  <span className="text-xs text-gray-500 font-medium block">Discount</span>
                  No Code Required
                </div>}
            </div>
          )}

          {/* Conditional Coupon Code Display */}
          {couponCode && discountType === "code" && (
            <div className="w-full bg-gray-50 border border-dashed border-gray-300 rounded-lg p-2 mb-3 flex items-center justify-between transition-all duration-200 hover:border-primary-500">
              <div className="flex flex-col items-start w-2/3">
                <p className="text-[10px] text-gray-500 mb-1 font-semibold">
                  Coupon Code
                </p>
                <p
                  className={`font-bold text-gray-900 text-sm transition-all duration-300 select-none ${copied === couponCode
                    ? "blur-none"
                    : "blur-[2px] bg-gradient-to-r from-gray-300 to-gray-100"
                    }`}
                  aria-label={
                    copied === couponCode ? couponCode : "Hidden coupon code"
                  }
                >
                  {couponCode}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopy(couponCode);
                }}
                className="flex items-center space-x-1 bg-white border border-gray-300 px-3 py-1 rounded-md text-gray-700 hover:bg-primary-50 hover:text-gray-900 transition-colors duration-200 text-xs font-medium shadow-sm"
              >
                <CopyIcon className="h-3 w-3" />
                <span>{copied === couponCode ? "Copied!" : "Copy"}</span>
              </button>
            </div>
          )}

          {/* Action Button and Original Badge Position */}
          <div className="w-full flex flex-col items-center">
            <div className="relative w-full flex justify-center mb-2">
              <button
                className={`w-full py-2 rounded-lg bg-primary-600 text-white font-bold cursor-pointer text-sm tracking-wide shadow-md 
                        hover:bg-primary-700 transition-all duration-200 hover:shadow-lg flex items-center justify-center`}
                onClick={handleViewClick}
              >
                <span className="relative z-10">
                  {couponCode && discountType === 'code' ? "GET CODE" : "VIEW DEAL"}
                </span>
              </button>
              {/* BADGE */}
              {badge.trim().length > 1 && (
                <span
                  className={`absolute -left-2 -top-2 ${badge === "EXCLUSIVE CODE"
                    ? "bg-gradient-to-br from-green-400 to-emerald-500"
                    : "bg-gradient-to-br from-cyan-400 to-primary-500"
                    } text-white text-xs px-2 py-0.5 rounded-tl-lg rounded-br-lg font-semibold shadow z-10`}
                >
                  {badge}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section: Stats & Expiry */}
        <div className="w-full flex justify-between items-center text-xs text-gray-500 border-t border-gray-100 px-4 py-2 bg-gray-50 rounded-b-xl mt-auto">
          {validTill && (
            <div className="flex gap-1 items-center text-gray-400">
              <ClockIcon className="h-3 w-3" />
              <span className="text-[10px]">Exp: {validTill}</span>
            </div>
          )}
          {rating && rating > 1 ? (
            <div className="flex items-center text-gray-400 space-x-1">
              <StarIcon className="h-3 w-3 text-yellow-400" />
              <span className="text-[10px] font-semibold">{rating} Rating</span>
            </div>
          ) : (
            <span className="text-[10px] text-gray-400 flex items-center gap-1">
              <span role="img" aria-label="smile">
                😊
              </span>
              <span role="img" aria-label="neutral">
                😐
              </span>
              <span role="img" aria-label="heart">
                🤍
              </span>
            </span>
          )}
          {/* {
            usedBy && <div>{usedBy}
              </div>
          } */}
        </div>
      </div>
    </div>
  );
};

export default OfferCard;